import { useCallback, useEffect, useRef, useState } from "react";
import { getPreference, setPreference } from "../../../data/preferencesClient.js";

export const PARTICIPANT_DISPLAY_KEY = "projects_participants_display";

// The matrix is keyed by authenticated identity. No browser-global cache:
// a previous user's pending reads/writes must not affect the next session.
export function useParticipantDisplayPreference(identity) {
  const [state, setState] = useState({ value: "initials", loading: true, saving: false, error: null });
  const alive = useRef(false);
  const failed = useRef("load");
  const load = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      if (!identity || identity === "unbound") throw new Error("Sign in to load your display preference.");
      const value = await getPreference(PARTICIPANT_DISPLAY_KEY);
      if (value !== null && value !== "initials" && value !== "names") throw new Error("Invalid saved participant display preference.");
      if (alive.current) setState({ value: value ?? "initials", loading: false, saving: false, error: null });
    } catch (err) {
      failed.current = "load";
      if (alive.current) setState(s => ({ ...s, loading: false, error: err.message }));
    }
  }, [identity]);
  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; };
  }, [load]);
  const save = useCallback(async value => {
    failed.current = value;
    setState(s => ({ ...s, value, saving: true, error: null }));
    try {
      if (!identity || identity === "unbound") throw new Error("Sign in to save your display preference.");
      await setPreference(PARTICIPANT_DISPLAY_KEY, value);
      if (alive.current) setState(s => ({ ...s, saving: false }));
    } catch (err) {
      if (alive.current) setState(s => ({ ...s, saving: false, error: err.message }));
    }
  }, [identity]);
  return { ...state, save, retry: () => failed.current === "load" ? load() : save(failed.current) };
}
